<script setup lang="ts">
import { useEditor, EditorContent } from '@tiptap/vue-3'
import StarterKit from '@tiptap/starter-kit'
import Image from '@tiptap/extension-image'
import { watch, onBeforeUnmount, ref } from 'vue'
import { UPLOAD_API_URL, STATIC_FILE_URL } from '../config'

interface Props {
  modelValue: string
  placeholder?: string
}

const props = withDefaults(defineProps<Props>(), {
  placeholder: '请输入内容...'
})

const emit = defineEmits<{
  'update:modelValue': [value: string]
  'imageClick': [url: string]
}>()

// 处理编辑器点击事件（图片预览）
const handleEditorClick = (event: MouseEvent) => {
  const target = event.target as HTMLElement
  if (target.tagName === 'IMG') {
    const imgSrc = target.getAttribute('src')
    if (imgSrc) {
      emit('imageClick', imgSrc)
    }
  }
}

// 文件输入引用
const fileInputRef = ref<HTMLInputElement | null>(null)

// 触发图片上传
const triggerImageUpload = () => {
  fileInputRef.value?.click()
}

// 处理图片上传
const handleImageUpload = async (event: Event) => {
  const target = event.target as HTMLInputElement
  const file = target.files?.[0]
  if (!file) return
  
  // 创建 FormData 并上传
  const formData = new FormData()
  formData.append('file', file)
  
  try {
    const response = await fetch(`${UPLOAD_API_URL}/api/upload/startup`, {
      method: 'POST',
      body: formData,
    })
    
    const result = await response.json()
    if (result.code === 1 && result.data) {
      // 插入图片到编辑器
      const filePath = result.data.src || result.data.file || result.data.url || result.data.path
      // 确保 STATIC_FILE_URL 末尾有斜线，filePath 开头没有斜线
      const baseUrl = STATIC_FILE_URL.endsWith('/') ? STATIC_FILE_URL : STATIC_FILE_URL + '/'
      const cleanPath = filePath.startsWith('/') ? filePath.slice(1) : filePath
      const imageUrl = `${baseUrl}${cleanPath}`
      editor.value?.chain().focus().setImage({ src: imageUrl }).run()
    } else {
      alert(result.msg || '上传图片失败')
    }
  } catch (error) {
    console.error('上传图片失败:', error)
    alert('上传图片失败，请稍后重试')
  } finally {
    // 清空文件输入
    if (fileInputRef.value) {
      fileInputRef.value.value = ''
    }
  }
}

const editor = useEditor({
  extensions: [
    StarterKit.configure({ 
      heading: { levels: [1, 2, 3] },
      bulletList: {},
      orderedList: {},
      blockquote: {},
      codeBlock: {},
      bold: {},
      italic: {},
      strike: {},
    }),
    Image,
  ],
  content: props.modelValue,
  onUpdate: ({ editor }) => {
    emit('update:modelValue', editor.getHTML())
  },
  editorProps: {
    attributes: {
      class: 'term-editor-content',
    },
  },
})

// 监听外部值变化
watch(() => props.modelValue, (newValue) => {
  if (editor.value && editor.value.getHTML() !== newValue) {
    editor.value.commands.setContent(newValue)
  }
})

onBeforeUnmount(() => {
  editor.value?.destroy()
})

// 暴露方法
defineExpose({
  getHTML: () => editor.value?.getHTML() || '',
  setContent: (content: string) => editor.value?.commands.setContent(content),
})
</script>

<template>
  <div class="term-editor-wrapper">
    <!-- 工具栏 -->
    <div class="term-toolbar">
      <button 
        @click="editor?.chain().focus().toggleBold().run()" 
        :class="{ active: editor?.isActive('bold') }"
        title="加粗"
      >B</button>
      <button 
        @click="editor?.chain().focus().toggleItalic().run()" 
        :class="{ active: editor?.isActive('italic') }"
        title="斜体"
      >I</button>
      <button 
        @click="editor?.chain().focus().toggleStrike().run()" 
        :class="{ active: editor?.isActive('strike') }"
        title="删除线"
      >S</button>
      <span class="toolbar-divider"></span>
      <button 
        @click="editor?.chain().focus().toggleHeading({ level: 1 }).run()" 
        :class="{ active: editor?.isActive('heading', { level: 1 }) }"
        title="标题1"
      >H1</button>
      <button 
        @click="editor?.chain().focus().toggleHeading({ level: 2 }).run()" 
        :class="{ active: editor?.isActive('heading', { level: 2 }) }"
        title="标题2"
      >H2</button>
      <button 
        @click="editor?.chain().focus().toggleHeading({ level: 3 }).run()" 
        :class="{ active: editor?.isActive('heading', { level: 3 }) }"
        title="标题3"
      >H3</button>
      <span class="toolbar-divider"></span>
      <button 
        @click="editor?.chain().focus().toggleBulletList().run()" 
        :class="{ active: editor?.isActive('bulletList') }"
        title="无序列表"
      >• 列表</button>
      <button 
        @click="editor?.chain().focus().toggleOrderedList().run()" 
        :class="{ active: editor?.isActive('orderedList') }"
        title="有序列表"
      >1. 列表</button>
      <span class="toolbar-divider"></span>
      <button 
        @click="editor?.chain().focus().toggleBlockquote().run()" 
        :class="{ active: editor?.isActive('blockquote') }"
        title="引用"
      >引用</button>
      <button 
        @click="editor?.chain().focus().toggleCodeBlock().run()" 
        :class="{ active: editor?.isActive('codeBlock') }"
        title="代码块"
      >代码</button>
      <span class="toolbar-divider"></span>
      <button class="image-btn" @click="triggerImageUpload" title="上传图片">📷</button>
    </div>
    
    <!-- 隐藏的文件输入 -->
    <input
      ref="fileInputRef"
      type="file"
      accept="image/*"
      style="display: none"
      @change="handleImageUpload"
    />
    
    <!-- 编辑器内容 -->
    <div class="term-editor-container">
      <EditorContent :editor="editor" class="term-rich-editor" @click="handleEditorClick" />
    </div>
  </div>
</template>

<style scoped>
.term-editor-wrapper {
  display: flex;
  flex-direction: column;
  height: 100%;
  overflow: hidden;
}

.term-toolbar {
  display: flex;
  align-items: center;
  gap: 0.25rem;
  padding: 0.5rem 0;
  flex-wrap: wrap;
}

.term-toolbar button {
  padding: 0.35rem 0.5rem;
  border: none;
  background: transparent;
  border-radius: 4px;
  font-size: 0.8rem;
  color: #666;
  cursor: pointer;
  transition: all 0.2s;
}

.term-toolbar button:hover {
  background: #e8e8e8;
}

.term-toolbar button.active {
  background: #42b883;
  color: #fff;
}

.toolbar-divider {
  width: 1px;
  height: 20px;
  background: #ddd;
  margin: 0 0.25rem;
}

.term-editor-container {
  flex: 1;
  overflow-y: auto;
  padding: 0.5rem 0;
}

/* 编辑器内容最大宽度 */
.term-rich-editor :deep(.term-editor-content) {
  outline: none;
  min-height: 200px;
  max-width: 520px;
  margin: 0 auto;
}

/* 图片样式 */
.term-rich-editor :deep(img) {
  max-width: 100%;
  height: auto;
  cursor: zoom-in;
  border-radius: 4px;
}

.term-rich-editor :deep(.term-editor-content p) {
  margin: 0.5rem 0;
  line-height: 1.6;
}

.term-rich-editor :deep(.term-editor-content h1) {
  font-size: 1.25rem;
  margin: 0.75rem 0 0.5rem;
}

.term-rich-editor :deep(.term-editor-content h2) {
  font-size: 1.1rem;
  margin: 0.625rem 0 0.5rem;
}

.term-rich-editor :deep(.term-editor-content h3) {
  font-size: 1rem;
  margin: 0.5rem 0 0.5rem;
}

.term-rich-editor :deep(.term-editor-content ul),
.term-rich-editor :deep(.term-editor-content ol) {
  margin: 0.5rem 0;
  padding-left: 1.5rem;
}

.term-rich-editor :deep(.term-editor-content blockquote) {
  border-left: 3px solid #42b883;
  padding-left: 1rem;
  margin: 0.5rem 0;
  color: #666;
}

.term-rich-editor :deep(.term-editor-content pre) {
  background: #f5f5f5;
  padding: 0.5rem;
  border-radius: 4px;
  overflow-x: auto;
}
</style>
